import { useEffect, useState } from 'react'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { CompanySettingsTargetReportsTable } from './CompanySettingsTargetsReportsTable'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import shallow from 'zustand/shallow'
import { CompanySettingsTargetReportsHeader } from './CompanySettingsTargetReportsHeader'
import { CompanySettingsTabs } from '../types'
import useCountrySettings from '../../../graphql/countrySettings/hooks/useCountrySettings'
import { useCompaniesAndCountriesOfCompanyManager } from '../../../graphql/company/hooks/useCompaniesAndCountriesOfCompanyManager'
import { useGetTargetReportsByCountryAndCompany } from '../../../graphql/reports/useQueriesReport'

export const CompanySettingsTargetsReportsTab = (): JSX.Element => {
  // Polling time in ms
  const DEFAULT_POLLING_REFRESH_TIME = 25
  // Target Report Scheeduler generation ratio
  const MAX_POLLING_REFERESH_TIME = 60000
  // Max value for polling count in order to not overcome the max polling time
  const MAX_POLLING_COUNT_VALUE = Math.round(MAX_POLLING_REFERESH_TIME / DEFAULT_POLLING_REFRESH_TIME)
  // Exponential growth ratio of polling count per each iteration (power 4 by default)
  const POLLING_COUNT_EXPONENTIAL_FACTOR = 4

  const [selected, activeTab] = useCompanySettingsStore((state) => [state.selected, state.activeTab], shallow)

  const [generationInProgress, setGenerationInProgress] = useState(false)
  const [pollingCount, setPollingCount] = useState(1)
  const [modalGenerationDisabled, setModalGenerationDisabled] = useState(false)

  const { data, error, loading, startPolling, stopPolling } = useGetTargetReportsByCountryAndCompany(selected.country, selected.companyId)

  const { data: countrySettings, error: settingsError, loading: settingsLoading } = useCountrySettings(selected.country)

  const {
    data: companyManagerCompanies,
    error: companyManagerCompaniesError,
    loading: companyManagerCompaniesLoading,
  } = useCompaniesAndCountriesOfCompanyManager()

  useEffect(() => {
    if (
      activeTab === CompanySettingsTabs.targetReports &&
      data &&
      !data.findTargetReportsByCountryAndCompany.some((report) => !report.createTargetReport) &&
      !modalGenerationDisabled &&
      (!countrySettings || countrySettings.getCountrySettings.createTargetReport)
    ) {
      setPollingCount(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeTab])

  useEffect(() => {
    stopPolling()
    if (
      activeTab === CompanySettingsTabs.targetReports &&
      data &&
      !data.findTargetReportsByCountryAndCompany.some((report) => !report.createTargetReport) &&
      !modalGenerationDisabled &&
      (!countrySettings || countrySettings.getCountrySettings.createTargetReport)
    ) {
      startPolling(DEFAULT_POLLING_REFRESH_TIME * pollingCount)
      return () => {
        stopPolling()
      }
    }
    return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPolling, stopPolling, pollingCount])

  useEffect(() => {
    if (
      activeTab === CompanySettingsTabs.targetReports &&
      pollingCount < MAX_POLLING_COUNT_VALUE &&
      data &&
      !data.findTargetReportsByCountryAndCompany.some((report) => !report.createTargetReport) &&
      !modalGenerationDisabled &&
      (!countrySettings || countrySettings.getCountrySettings.createTargetReport)
    ) {
      setTimeout(() => {
        if (pollingCount * POLLING_COUNT_EXPONENTIAL_FACTOR <= MAX_POLLING_COUNT_VALUE) {
          setPollingCount(pollingCount * POLLING_COUNT_EXPONENTIAL_FACTOR)
        } else {
          setPollingCount(MAX_POLLING_COUNT_VALUE)
        }
      }, DEFAULT_POLLING_REFRESH_TIME * pollingCount)
    }
  })

  if (
    loading ||
    settingsLoading ||
    companyManagerCompaniesLoading ||
    error ||
    settingsError ||
    companyManagerCompaniesError ||
    !data ||
    !data.findTargetReportsByCountryAndCompany ||
    !companyManagerCompanies ||
    !companyManagerCompanies.findCompaniesAndCountriesByCompanyManager ||
    !countrySettings ||
    !countrySettings.getCountrySettings
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || settingsLoading || companyManagerCompaniesLoading}
        hasNoData={!data || !countrySettings || !companyManagerCompanies}
        error={error || settingsError || companyManagerCompaniesError}
      />
    )
  }

  return (
    <>
      <CompanySettingsTargetReportsHeader
        hrSettings={false}
        companyId={selected.companyId}
        companyName={selected.companyName}
        country={selected.country}
        companiesAsCompanyManager={companyManagerCompanies}
        generationInProgress={generationInProgress}
        generationDisabled={
          (data && data.findTargetReportsByCountryAndCompany.some((report) => !report.createTargetReport)) ||
          (countrySettings && !countrySettings.getCountrySettings.createTargetReport) ||
          modalGenerationDisabled
        }
        setGenerationInProgress={setGenerationInProgress}
        setPollingCount={setPollingCount}
        setModalGenerationDisabled={setModalGenerationDisabled}
      />
      <CompanySettingsTargetReportsTable
        tableData={data.findTargetReportsByCountryAndCompany}
        companyName={selected.companyName}
        country={selected.country}
        setGenerationInProgress={setGenerationInProgress}
      />
    </>
  )
}
